import { ref, onUnmounted } from 'vue'
import { getEcho, initEcho } from '@/services/echo'
import { useAuthStore } from '@/stores/auth'
import { useBrowserNotifications } from '@/composables/useBrowserNotifications'
import { useRouter } from 'vue-router'
import Swal from 'sweetalert2'

export function useRealtimeNotifications() {
  const latestNotification = ref(null)
  const router = useRouter()
  let channel = null
  function showDesktopNotif(notif) {
    const { show, permission } = useBrowserNotifications()
    console.log('[Pusher] Received notification:', notif, '| Permission:', permission())

    // Vibration feedback (works on Android)
    if ('vibrate' in navigator) {
      navigator.vibrate(200)
    }

    const resolveUrl = (targetNotif) => {
      if (targetNotif.data && (targetNotif.data.type === 'review_reminder' || targetNotif.data.rate_order)) {
        if (targetNotif.data.commande_id) {
          return `/panel/orders?rate_order=${targetNotif.data.commande_id}`
        }
        if (targetNotif.data.physical_order_id) {
          return `/panel/physical-orders`
        }
      }

      let targetUrl = targetNotif.action_url || ''
      if (targetUrl.startsWith('http://') || targetUrl.startsWith('https://')) {
        try {
          const parsed = new URL(targetUrl)
          targetUrl = parsed.pathname + parsed.search + parsed.hash
        } catch {
          const idx = targetUrl.indexOf('/panel/')
          if (idx !== -1) targetUrl = targetUrl.substring(idx)
        }
      }

      if ((targetUrl === '/panel/orders' || !targetUrl) && targetNotif.data && targetNotif.data.commande_id) {
        targetUrl = `/panel/orders?id=esk-${targetNotif.data.commande_id}`
      }

      return targetUrl || '/panel/orders'
    }

    const navigateTo = (targetNotif) => {
      const u = resolveUrl(targetNotif)
      if (u) {
        if (u.startsWith('http://') || u.startsWith('https://')) {
          window.location.href = u
        } else {
          router.push(u)
        }
      }
    }

    // In-app toast (works on all platforms, including mobile)
    Swal.fire({
      icon: 'info',
      title: notif.title || 'New notification',
      text: notif.message || '',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 5000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.onclick = () => {
          Swal.close()
          navigateTo(notif)
        }
      },
    })

    // Desktop notification (browser Notification API - unreliable on mobile)
    if (permission() !== 'granted') return

    const n = show(notif.title || 'New notification', {
      body: notif.message || '',
      tag: `notif-${notif.id}`,
      requireInteraction: true,
      data: { action_url: notif.action_url, id: notif.id },
    })

    if (n) {
      console.log('[Pusher] Desktop notification shown:', notif.title)
      n.onclick = () => {
        n.close()
        window.focus()
        navigateTo(notif)
      }
    } else {
      console.warn('[Pusher] Failed to show desktop notification')
    }
  }

  function subscribe() {
    const authStore = useAuthStore()
    authStore.initAuth()
    const user = authStore.user
    const token = authStore.token

    if (!user || !token) {
      console.warn('[Pusher] Cannot subscribe: no user or token')
      return
    }

    if (user.notifications_realtime === false) {
      console.log('[Pusher] Realtime notifications disabled for user', user.id)
      return
    }

    console.log('[Pusher] Subscribing user:', user.id)

    initEcho(token)
    const echo = getEcho()
    if (!echo) {
      console.warn('[Pusher] Echo not initialized (Pusher key missing?)')
      return
    }

    console.log('[Pusher] Echo initialized, subscribing to user.' + user.id)

    channel = echo.private(`user.${user.id}`)

    channel.listen('.notification.created', (data) => {
      console.log('[Pusher] Event received:', data)
      latestNotification.value = { ...data }
      showDesktopNotif(data)
    })
  }

  function unsubscribe() {
    if (channel) {
      try {
        channel.stopListening('.notification.created')
        channel.unsubscribe()
      } catch {
        // connection already closed
      }
      channel = null
    }
  }

  onUnmounted(() => {
    try {
      unsubscribe()
    } catch {
      // ignore unmount errors
    }
    Swal.close()
  })

  return {
    latestNotification,
    subscribe,
    unsubscribe,
  }
}
